import React, { useState, useEffect, useRef } from 'react';
import Header from './Header';
import { useNavigate, useLocation } from 'react-router-dom';
import Footer from './Footer';
import Aside from './Aside';
import axios from 'axios';

const History = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [userHistoryPeriod, setUserHistoryPeriod] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15); // Added for accurate pagination
  const [totalItems, setTotalItems] = useState(0); // Added for total count
  const navigate = useNavigate();
  const location = useLocation();
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState('Download');

  useEffect(() => {
    const fetchHistoryPeriod = async (page) => {
      setIsLoading(true); // Moved inside fetch
      if (!page) {
        page = 1;
      }
      try {
        const authData = localStorage.getItem("auth");
        const token = authData ? JSON.parse(authData).token : null;
        const user_id = authData ? JSON.parse(authData).id : null;
        const _type = "social_media_history";
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/history`,
          {
            params: {
              user_id: user_id,
              _token: token,
              type: _type,
              page: page,
            },
            headers: {
              "Content-Type": "application/json; charset=utf-8",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data.status === 200) {
          const historyData = Array.isArray(response.data.history.data)
            ? response.data.history.data
            : Object.values(response.data.history.data);
          setUserHistoryPeriod(historyData);
          setCurrentPage(response.data.history.current_page);
          setTotalPages(response.data.history.last_page);
          setItemsPerPage(response.data.history.per_page); // Added
          setTotalItems(response.data.history.total); // Added
          if (currentPage !== page) {
            navigate(`/history?page=${page}`, { replace: true }); // Added replace: true
          }
        } else if (response.data.status === 401) {
          alert("Unauthorized");
        }
      } catch (error) {
        console.error("Error fetching history:", error);
      } finally {
        setIsLoading(false);
      }
    };

    const queryParams = new URLSearchParams(location.search);
    const page = parseInt(queryParams.get("page"), 10) || 1;
    fetchHistoryPeriod(page);
  }, [location.search, navigate]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      navigate(`/history?page=${page}`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const options = {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone: 'UTC'
    };
    const formattedDate = date.toLocaleString('en-US', options);
    return `${formattedDate} `;
  };

  const handleModalOpen = (content) => {
    setModalContent(content);
    setIsModalOpen(true);
  };

  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [modalContent, setModalContent] = useState([]);

  const handleButtonClick = (display_output, category, categoryName, event) => {
    const modalContent = {
      display_output: display_output,
      category,
      categoryName,
      event,
    };
    setModalContent(modalContent);
    fetchProcessingData(modalContent);
    setIsModalOpen(true);
  };

  useEffect(() => {
    if (modalContent) {
      setModalContent(modalContent);
    }
  }, [modalContent]);

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedImage(null);
    setSelectedAvatar(null);
  };

  const handleBackdropClick = (e) => {
    if (e.target.id === 'modalBackdrop') {
      setIsModalOpen(false);
      setSelectedImage(null);
      setSelectedAvatar(null);
    }
  };

  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isCopied, setIsCopied] = useState(false);

  const handleImageClick = (img) => {
    setSelectedImage(img);
  };

  const downloadImage = (src, img1) => {
    const imageToOpen = selectedImage || img1;
    if (!imageToOpen) {
      console.error('No image available to open.');
      setDownloadStatus('Download');
      return;
    }
    try {
      setDownloadStatus('Opening...');
      const newTab = window.open(imageToOpen, '_blank');
      if (!newTab) {
        alert('Popup blocked. Please allow popups for this website.');
      }
      setDownloadStatus('Download');
    } catch (error) {
      console.error('Error opening the image:', error);
      setDownloadStatus('Download');
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadImagebutton = async (src) => {
    const imageToDownload = selectedImage || src;
    if (!imageToDownload) {
      console.error('No image available to download.');
      setDownloadStatus('No image available');
      return;
    }
    const filename = imageToDownload.split('/').pop();
    setIsDownloading(true);
    setDownloadStatus('Downloading...');
    const urlSegments = src ? src.split('/') : [];
    const file_id = urlSegments.length > 1 ? urlSegments[urlSegments.length - 2] : 'unknown';
    const authData = localStorage.getItem('auth');
    const token = authData ? JSON.parse(authData).token : null;
    const user_id = authData ? JSON.parse(authData).id : null;
    const apiUrl = `${process.env.REACT_APP_API_URL}/api/download-image/${filename}?user_id=${user_id}&file_id=${file_id}&type=mid-images`;
    try {
      const response = await axios.get(apiUrl, {
        headers: {
          "Authorization": `Bearer ${token}`,
        },
        responseType: 'blob',
      });
      const blob = response.data;
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
      setDownloadStatus('Downloaded');
    } catch (error) {
      console.error('Error fetching data:', error);
      setDownloadStatus('Download Failed');
    } finally {
      setTimeout(() => {
        setIsDownloading(false);
        setDownloadStatus('Download');
      }, 2000);
    }
  };

  const downloadAvatar = () => {
    const defaultImage = modalContent.display_output.find(item => item.source_image)?.source_image;
    const imageToOpen = selectedAvatar || defaultImage;
    if (!imageToOpen) {
      console.error('No avatar available to open.');
      setDownloadStatus('Download');
      return;
    }
    try {
      setDownloadStatus('Opening...');
      const newTab = window.open(imageToOpen, '_blank');
      if (!newTab) {
        alert('Popup blocked. Please allow popups for this website.');
      }
      setDownloadStatus('Download');
    } catch (error) {
      console.error('Error opening the avatar:', error);
      setDownloadStatus('Download');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleAvatarClick = (img) => {
    setSelectedAvatar(img);
  };

  const downloadAvatarbutton = async () => {
    const defaultImage = modalContent.display_output.find(item => item.source_image)?.source_image;
    const imageToDownload = selectedAvatar || defaultImage;
    const filename = imageToDownload.split('/').pop();
    const urlSegments = imageToDownload.split('/');
    const file_id = urlSegments[urlSegments.length - 2];
    const authData = localStorage.getItem('auth');
    const token = authData ? JSON.parse(authData).token : null;
    const user_id = authData ? JSON.parse(authData).id : null;
    const apiUrl = `${process.env.REACT_APP_API_URL}/api/download-image/${filename}?user_id=${user_id}&file_id=${file_id}&type=faceswap`;
    try {
      setIsDownloading(true);
      setDownloadStatus('Downloading...');
      const response = await axios.get(apiUrl, {
        headers: {
          "Authorization": `Bearer ${token}`,
        },
        responseType: 'blob',
      });
      const blob = response.data;
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename || 'downloaded-image';
      link.click();
      URL.revokeObjectURL(link.href);
      setDownloadStatus('Downloaded');
    } catch (error) {
      console.error('Error downloading the image:', error);
      setDownloadStatus('Download Failed');
    } finally {
      setTimeout(() => {
        setIsDownloading(false);
        setDownloadStatus('Download');
      }, 2000);
    }
  };

  const [selectedContent, setSelectedContent] = useState("");

  const handleRadioChange = (content) => {
    setSelectedContent(content);
  };

  const handleCopy = () => {
    const selectedItem = modalContent.display_output.find(
      (item) => item.message.content === selectedContent
    );
    if (selectedItem) {
      const selectedText = selectedItem.message.content;
      setIsLoading(true);
      navigator.clipboard
        .writeText(selectedText)
        .then(() => {
          setIsLoading(false);
          setIsCopied(true);
          setTimeout(() => setIsCopied(false), 2000);
        })
        .catch((err) => {
          setIsLoading(false);
          console.error("Failed to copy text: ", err);
        });
    }
  };

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isModalOpen]);

  const downloadVideo = async (event) => {
    const imageToDownload = event.display_output;
    if (!imageToDownload) {
      console.error('No image available to download.');
      setDownloadStatus('No image available');
      return;
    }
    const filename = imageToDownload.split('/').pop();
    setDownloadStatus('Downloading...');
    const urlSegments = imageToDownload ? imageToDownload.split('/') : [];
    const file_id = urlSegments.length > 1 ? urlSegments[urlSegments.length - 2] : 'unknown';
    const authData = localStorage.getItem('auth');
    const token = authData ? JSON.parse(authData).token : null;
    const user_id = authData ? JSON.parse(authData).id : null;
    const apiUrl = `${process.env.REACT_APP_API_URL}/api/download-image/${filename}?user_id=${user_id}&file_id=${file_id}&type=videos`;
    try {
      const response = await axios.get(apiUrl, {
        headers: {
          "Authorization": `Bearer ${token}`,
        },
        responseType: 'blob',
      });
      const blob = response.data;
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
      setDownloadStatus('Downloaded');
    } catch (error) {
      console.error('Error fetching data:', error);
      setDownloadStatus('Download Failed');
    } finally {
      setTimeout(() => {
        setDownloadStatus('Download');
      }, 2000);
    }
  };

  const fetchProcessingData = async (modalContent) => {
    try {
      const genId = modalContent?.event?.pred_id;
      try {
        const authData = localStorage.getItem("auth");
        const token = authData ? JSON.parse(authData).token : null;
        const id = authData ? JSON.parse(authData).id : null;

        const statusResponse = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/generate/status/${genId}`,
          {
            _token: token,
            user_id: id,
            type: "videos",
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const rspData = statusResponse.data;
        if (rspData.status === 200) {
          setModalContent((prevContent) => ({
            ...prevContent,
            display_output: modalContent.display_output,
            event: {
              ...prevContent.event,
              status: 'succeeded',
              display_output: modalContent.display_output,
            },
          }));
          return;
        } else if (rspData.status === 422) {
          setModalContent((prevContent) => ({
            ...prevContent,
            display_output: modalContent.display_output,
            event: {
              ...prevContent.event,
              status: 'failed',
              display_output: modalContent.display_output,
            },
          }));
          return;
        } else if (rspData.status === 102) {
          setModalContent((prevContent) => ({
            ...prevContent,
            display_output: rspData.output ?? prevContent.display_output,
            status: rspData.genStatus ?? prevContent.status,
          }));
          await new Promise((resolve) => setTimeout(resolve, 5000));
        }
      } catch (error) {
        console.error("Error occurred while polling for status:", error);
      }
    } catch (error) {
      console.error("Error occurred while fetching processing data:", error);
    }
  };

  return (
    <div>
      <Header />
      <div style={{ display: 'flex' }}>
        <div style={{ zIndex: '101', background: 'white', boxShadow: '0 .8rem 1rem rgba(0, 0, 0, .15)', position: 'fixed', height: '100vh' }}>
          <Aside />
        </div>
        <div className='mainstylle'>
          <section className="generate-main paddtop">
            <div className="container py-4 px-2">
              <div className="row histroy">
                <div className="col-md-12">
                  <h1 className="text-left fw-bold pb-5 text-dark heading-50 ">History</h1>
                  <div className="alert alert-warning alert-dismissible fade show" role="alert">
                    <strong>
                      <h3 className="mb-4">Note:-</h3>
                    </strong>
                    <p className="ms-6">
                      <span>
                        History is stored for a maximum of <b>7 days</b>, after which it will be{' '}
                        <b>permanently deleted</b> from the system.
                      </span>
                      <br />
                      Please ensure that you download any necessary data within this timeframe to avoid losing it.
                    </p>
                  </div>

                  <div className="card rounded-4" style={{ background: 'white' }}>
                    <div className="card-header card-header-stretch border-bottom border-gray-200 " style={{ background: 'white', borderTopRightRadius: '10px', borderTopLeftRadius: '10px' }}>
                      <div className="card-title">
                        <h3 className="fw-bold m-0">History</h3>
                      </div>
                    </div>

                    <div id="kt_billing_months" className="card-body p-0 tab-pane fade show active">
                      <div className="table-responsive">
                        <div className='sloader-social-media-history'>
                          <table className="table table-row-bordered align-middle gy-4 gs-9">
                            <thead className='border-bottom border-gray-200 fs-6 text-gray-600 fw-bold bg-light bg-opacity-75'>
                              <tr>
                                <th>#</th>
                                <th>Date</th>
                                <th>Category</th>
                                <th>Prompt</th>
                                <th>Uploaded Image</th>
                                <th>Result</th>
                              </tr>
                            </thead>
                            <tbody className="social_media_history">
                              {Array.isArray(userHistoryPeriod) && userHistoryPeriod.length > 0 ? (
                                [...userHistoryPeriod].reverse().map((event, index) => {
                                  const rowNumber = (currentPage - 1) * itemsPerPage + (index + 1); // Updated for pagination
                                  return (
                                    <tr key={index}>
                                      <td>{rowNumber}</td>
                                      <td>{formatDate(event.created_at)}</td>
                                      <td>{event.category_name || 'N/A'}</td>
                                      <td style={{ width: '40%' }}>{event.prompt || 'N/A'}</td>
                                      <td>
                                        {event.category === 3 ? (
                                          event.source_image && event.target_image ? (
                                            <div style={{ display: 'flex', gap: '10px' }}>
                                              <div>
                                                <p>Target image</p>
                                                <img
                                                  style={{ width: '100px', height: '80px', borderRadius: '3px' }}
                                                  src={event.display_output[0].source_image}
                                                  alt="Source Event"
                                                />
                                              </div>
                                              <div>
                                                <p>Your image</p>
                                                <img
                                                  style={{ width: '100px', height: '80px', borderRadius: '3px' }}
                                                  src={event.display_output[1].target_image}
                                                  alt="Target Event"
                                                />
                                              </div>
                                            </div>
                                          ) : (
                                            'No image Uploaded'
                                          )
                                        ) : event.category === 1 || event.category === 2 || event.category === 4 ? (
                                          event.image ? (
                                            <img
                                              style={{ width: '100px', height: '80px', borderRadius: '3px' }}
                                              src={event.image_src}
                                              alt="Event"
                                            />
                                          ) : (
                                            'No image Uploaded'
                                          )
                                        ) : (
                                          'Category not handled'
                                        )}
                                      </td>
                                      <td>
                                        <button
                                          className="btn btn-sm btn-theme btn-shadow btn-shadow"
                                          onClick={() =>
                                            handleButtonClick(
                                              event.display_output || 'N/A',
                                              event.category,
                                              event.category_name || 'Unknown Category',
                                              event,
                                            )
                                          }
                                        >
                                          View
                                        </button>
                                      </td>
                                    </tr>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td colSpan="9" style={{ textAlign: "center" }}>No history available.</td>
                                </tr>
                              )}
                            </tbody>
                            <tr></tr>
                          </table>
                        </div>
                      </div>
                    </div>

                    {userHistoryPeriod.length === 0 ? (
                      <span className="text-muted" style={{ marginLeft: '20px' }}>
                      </span>
                    ) : (
                      <>
                        <p style={{ paddingLeft: '20px' }}>
                          Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} entries
                        </p>
                        <div className="d-flex justify-content-center mt-2 pb-5">
                          <nav aria-label="Page navigation">
                            <ul className="pagination">
                              {currentPage > 1 && (
                                <li className="page-item">
                                  <button
                                    className="page-link"
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    aria-label="Previous"
                                  >
                                    « Previous
                                  </button>
                                </li>
                              )}
                              {[...Array(totalPages)].map((_, index) => {
                                const page = index + 1;
                                return (
                                  <li
                                    className={`page-item ${currentPage === page ? "active" : ""}`}
                                    key={page}
                                  >
                                    <button
                                      className="page-link"
                                      onClick={() => handlePageChange(page)}
                                      aria-current={currentPage === page ? "page" : undefined}
                                    >
                                      {page}
                                    </button>
                                  </li>
                                );
                              })}
                              {currentPage < totalPages && (
                                <li className="page-item">
                                  <button
                                    className="page-link"
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    aria-label="Next"
                                  >
                                    Next »
                                  </button>
                                </li>
                              )}
                            </ul>
                          </nav>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Modal */}
            {isModalOpen && modalContent.category === 1 && (
              <div
                id="modalBackdrop"
                onClick={handleBackdropClick}
                className="modal fade show"
                style={{ display: 'block' }}
                aria-labelledby="exampleModalLabel" >
                <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '500px !Important' }}>
                  <div className="modal-content" style={{ maxWidth: '500px !Important' }}>
                    <div className="modal-header">
                      <h1 className="modal-title fs-5" id="exampleModalLabel">
                        Result
                      </h1>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={handleModalClose}
                        aria-label="Close"
                      ></button>
                    </div>
                    <div className="modal-body history-modal-body">
                      {modalContent.display_output && modalContent.display_output.length > 0 ? (
                        modalContent.display_output.map((item, index) => (
                          <div key={index} style={{ width: '100%' }}>
                            <div style={{ textAlign: 'center', marginBottom: '8px' }}>
                              <div className="category-1">
                                <div className='col-md-12'>
                                  <div
                                    className="mb-3"
                                    style={{
                                      border: selectedContent === item.message.content ? '1px solid #1179fc' : '1px solid gray', borderRadius: '5px'
                                    }}
                                  >
                                    <input
                                      type="radio"
                                      checked={selectedContent === item.message.content}
                                      name="options"
                                      className="btn-check"
                                      id={`radio-${item.message.content}`}
                                      onChange={() => handleRadioChange(item.message.content)}
                                    />
                                    <label
                                      htmlFor={`radio-${item.message.content}`}
                                      className={`btn result-text w-100 text-start bordered d-flex align-items-center justify-content-between ${selectedContent === item.message.content ? 'text-blue' : ''}`}>
                                      <span className="text-start d-grid">
                                        <p className="mb-0 caption-result-body"
                                          style={{
                                            fontSize: "15px", fontWeight: "500",
                                            color: selectedContent === item.message.content ? '#1179fc' : 'black',
                                          }}>
                                          {item.message.content}
                                        </p>
                                      </span>
                                      <span className="not-check material-icons md-20">
                                        <i
                                          className={`ps-1 pe-1 bi ${selectedContent === item.message.content ? "bi-check-square-fill" : "bi-square"}`}
                                          style={{ fontSize: "18px", color: selectedContent === item.message.content ? "#1179fc" : "gray" }}
                                        ></i>
                                      </span>
                                      <span className="check material-icons md-20">
                                        <i className="ps-1 pe-1 bi bi-check-square-fill" style={{ fontSize: "18px", color: "#1179fc" }}></i>
                                      </span>
                                    </label>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p>No data available</p>
                      )}
                    </div>
                    <div style={{ display: 'flex', marginLeft: ' 12px' }}>
                      <button
                        onClick={handleCopy}
                        className="btn btn-theme-rev btn-shadow-rev copy-btn-caption-result">
                        <span className="copy-text-caption-result">
                          {isLoading ? "Loading..." : isCopied ? "Copied!" : "Copy"} </span>
                        <i className={`bi bi-clipboard ${isLoading ? 'loading' : ''}`}></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {isModalOpen && modalContent.category === 2 && (
              <div
                id="modalBackdrop"
                onClick={handleBackdropClick}
                className="modal fade show"
                style={{ display: 'block' }}
                aria-labelledby="exampleModalLabel">
                <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h1 className="modal-title fs-5" id="exampleModalLabel">
                        Result
                      </h1>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={handleModalClose}
                        aria-label="Close"
                      ></button>
                    </div>

                    <div className="modal-body history-modal-body" style={{ display: 'flex', flexDirection: 'row' }}>
                      <div style={{ width: '100%' }}>
                        {modalContent.display_output && modalContent.display_output.length > 0 ? (
                          modalContent.display_output.map((item, index) => (
                            <div key={index} style={{ width: '100%' }}>
                              <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                                {item.img1 && (
                                  <img
                                    src={selectedImage || item.img1}
                                    alt="Selected Image"
                                    className='imghover'
                                    style={{
                                      width: '100%', objectFit: 'cover',
                                      borderRadius: '6px',
                                    }}
                                    onClick={() => downloadImage(item.img1, item.img1)}
                                  />
                                )}
                              </div>
                            </div>
                          ))
                        ) : (
                          <p>No data available</p>
                        )}
                      </div>
                    </div>

                    <div className="modal-body history-modal-body" style={{ display: 'flex', flexDirection: 'row' }}>
                      {modalContent.display_output && modalContent.display_output.length > 0 ? (
                        modalContent.display_output.map((item, index) => (
                          <div key={index} style={{ width: '100%' }}>
                            <div className='px-1' style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
                              {item.img1 && (
                                <img
                                  src={item.img1}
                                  alt="Image 1" className='modalimagesheight'
                                  style={{
                                    cursor: 'pointer',
                                    objectFit: 'cover',
                                    borderRadius: '6px',
                                    boxShadow: selectedImage === null || selectedImage === item.img1 ? '0 15px 45px rgb(254 54 59 / 35%)' : 'none',
                                    filter: selectedImage === null || selectedImage === item.img1 ? 'brightness(70%)' : 'none',
                                    border: selectedImage === null || selectedImage === item.img1 ? '3px solid red' : 'none',
                                  }}
                                  onClick={() => handleImageClick(item.img1)}
                                />
                              )}
                              {item.img2 && (
                                <img
                                  src={item.img2}
                                  alt="Image 2" className='modalimagesheight'
                                  style={{
                                    cursor: 'pointer',
                                    borderRadius: '6px',
                                    filter: selectedImage === item.img2 ? 'brightness(70%)' : 'none',
                                    border: selectedImage === item.img2 ? '3px solid red' : 'none',
                                  }}
                                  onClick={() => handleImageClick(item.img2)}
                                />
                              )}
                              {item.img3 && (
                                <img
                                  src={item.img3}
                                  alt="Image 3" className='modalimagesheight'
                                  style={{
                                    cursor: 'pointer',
                                    borderRadius: '6px',
                                    filter: selectedImage === item.img3 ? 'brightness(70%)' : 'none',
                                    border: selectedImage === item.img3 ? '3px solid red' : 'none',
                                  }}
                                  onClick={() => handleImageClick(item.img3)}
                                />
                              )}
                              {item.img4 && (
                                <img
                                  src={item.img4}
                                  alt="Image 4" className='modalimagesheight'
                                  style={{
                                    cursor: 'pointer',
                                    borderRadius: '6px',
                                    filter: selectedImage === item.img4 ? 'brightness(70%)' : 'none',
                                    border: selectedImage === item.img4 ? '3px solid red' : 'none',
                                  }}
                                  onClick={() => handleImageClick(item.img4)}
                                />
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <p>No data available</p>
                      )}
                    </div>

                    <div style={{ display: 'flex', marginLeft: '20px', width: '43% !important' }}>
                      <button
                        onClick={() => downloadImagebutton(modalContent.display_output[0]?.img1)}
                        className="btn btn-theme-rev btn-shadow-rev img-download-btn" disabled={isDownloading}>
                        {downloadStatus} <i className="bi bi-download"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {isModalOpen && modalContent.category === 3 && (
              <div
                id="modalBackdrop"
                onClick={handleBackdropClick}
                className="modal fade show"
                style={{ display: 'block' }}
                aria-labelledby="exampleModalLabel">
                <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h1 className="modal-title fs-5" id="exampleModalLabel">
                        Result
                      </h1>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={handleModalClose}
                        aria-label="Close"
                      ></button>
                    </div>

                    <div className="modal-body history-modal-body" style={{ display: 'flex', flexDirection: 'row' }}>
                      <div style={{ width: '100%' }}>
                        {modalContent.display_output && modalContent.display_output.length > 0 ? (
                          modalContent.display_output.map((item, index) => (
                            <div key={index} style={{ width: '100%' }}>
                              <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }} >
                                {item.source_image && (
                                  <img
                                    src={selectedAvatar || item.source_image}
                                    alt="Selected Image"
                                    className='imghover'
                                    style={{
                                      width: '100%', objectFit: 'cover',
                                      borderRadius: '6px',
                                    }}
                                    onClick={() => downloadAvatar(selectedAvatar || item.source_image)}
                                  />
                                )}
                              </div>
                            </div>
                          ))
                        ) : (
                          <p>No data available</p>
                        )}
                      </div>
                    </div>

                    <div className="modal-body history-modal-body" style={{ display: 'flex', flexDirection: 'row' }}>
                      {modalContent.display_output && modalContent.display_output.length > 0 ? (
                        modalContent.display_output.map((item, index) => (
                          <div key={index} style={{ width: '100%' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }} className='px-1'>
                              {item.source_image && (
                                <img
                                  src={item.source_image}
                                  alt="Image 1" className='modalimagesheight'
                                  style={{
                                    cursor: 'pointer',
                                    objectFit: 'cover',
                                    borderRadius: '6px',
                                    boxShadow: selectedAvatar === null || selectedAvatar === item.source_image ? '0 15px 45px rgb(254 54 59 / 35%)' : 'none',
                                    filter: selectedAvatar === null || selectedAvatar === item.source_image ? 'brightness(70%)' : 'none',
                                    border: selectedAvatar === null || selectedAvatar === item.source_image ? '3px solid red' : 'none',
                                  }}
                                  onClick={() => handleAvatarClick(item.source_image)}
                                />
                              )}
                              {item.target_image && (
                                <img
                                  src={item.target_image}
                                  alt="Image 2" className='modalimagesheight'
                                  style={{
                                    cursor: 'pointer',
                                    borderRadius: '6px',
                                    boxShadow: selectedAvatar === item.target_image ? '0 15px 45px rgb(254 54 59 / 35%)' : 'none',
                                    filter: selectedAvatar === item.target_image ? 'brightness(70%)' : 'none',
                                    border: selectedAvatar === item.target_image ? '3px solid red' : 'none',
                                  }}
                                  onClick={() => handleAvatarClick(item.target_image)}
                                />
                              )}
                              {item.output && (
                                <img
                                  src={item.output}
                                  alt="Image 3" className='modalimagesheight'
                                  style={{
                                    cursor: 'pointer',
                                    borderRadius: '6px',
                                    boxShadow: selectedAvatar === item.output ? '0 15px 45px rgb(254 54 59 / 35%)' : 'none',
                                    filter: selectedAvatar === item.output ? 'brightness(70%)' : 'none',
                                    border: selectedAvatar === item.output ? '3px solid red' : 'none',
                                  }}
                                  onClick={() => handleAvatarClick(item.output)}
                                />
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <p>No data available</p>
                      )}
                    </div>

                    <div style={{ display: 'flex', marginLeft: '20px', width: '43% !important' }}>
                      <button
                        onClick={downloadAvatarbutton}
                        className="btn btn-theme-rev btn-shadow-rev img-download-btn"
                        disabled={isDownloading}
                      >
                        {downloadStatus} <i className="bi bi-download"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {isModalOpen && modalContent.category === 4 && (
              <div
                id="modalBackdrop"
                onClick={handleBackdropClick}
                className="modal fade show"
                style={{ display: 'block' }}
                aria-labelledby="exampleModalLabel">
                <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h1 className="modal-title fs-5" id="exampleModalLabel">
                        Result
                      </h1>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={handleModalClose}
                        aria-label="Close"
                      ></button>
                    </div>

                    <div className="modal-body history-modal-body" style={{ display: 'flex', flexDirection: 'row' }}>
                      <div style={{ width: '100%' }}>
                        {modalContent && modalContent.event ? (
                          <div style={{ width: '100%' }}>
                            <div style={{ display: 'flex', justifyContent: 'center', width: '100%', flexDirection: 'column' }}>
                              <video controls src={modalContent?.event?.status === 'succeeded' ? modalContent.event.display_output : ""}
                                style={{ width: '100%', maxWidth: '600px', borderRadius: '6px' }}>
                                <source
                                  src={modalContent?.event?.status === 'succeeded' ? modalContent.event.display_output : ""}
                                  type="video/mp4" />
                                Your browser does not support the video tag.
                              </video>
                            </div>
                          </div>
                        ) : (
                          <p>No data available</p>
                        )}
                      </div>
                    </div>
                    {modalContent.event.status === 'succeeded' && (
                      <div className="p-3 pt-0">
                        <div className="bg-light px-5 py-3">
                          <p className="fw-bold txt-primary text-center fs-7">
                            <i className="ki-duotone ki-check-circle text-success fs-5x">
                              <span className="path1"></span>
                              <span className="path2"></span>
                            </i>
                          </p>
                          <div className="mt-3 text-center">Video successfully generated</div>
                          <div className="mt-3 text-center">
                            <button
                              type="button"
                              disabled={isDownloading}
                              onClick={() => downloadVideo(modalContent.event)}
                              className="btn btn-theme-rev btn-shadow-rev video-download-btn"
                            >
                              <span id="download-btn-text">{downloadStatus}</span>
                              <i className="bi bi-download"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {modalContent.event.status === 'failed' && (
                      <div className="p-3 pt-0">
                        <div className="alert alert-danger">
                          <div className="bg-light p-5">
                            <p className="fw-bold txt-primary text-center fs-7">
                              <i className="ki-duotone ki-cross-circle text-danger fs-5x">
                                <span className="path1"></span><span className="path2"></span>
                              </i>
                            </p>
                            <div className="mt-3 text-center">The video has been marked as failed.</div>
                            <p></p>
                          </div>
                        </div>
                      </div>
                    )}

                    {modalContent.event.status !== 'failed' && modalContent.event.status !== 'succeeded' && (
                      <div className="p-3 pt-0">
                        <div className="bg-light p-5">
                          <p className="fw-bold txt-primary text-center fs-2">Please wait</p>
                          <p className="fw-bold txt-primary text-center fs-4">This will take 2 minutes.</p>
                          <p className="txt-primary text-center fs-7">
                            <span className="spinner-border spinner-border-sm align-middle ms-2"> </span>
                            processing
                          </p>
                          <p className="txt-primary text-center fs-7 mt-3">
                            <b>NOTE:-</b> Kindly avoid refreshing your page during this time, as refreshing may result in data loss.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </section>
          <Footer />
        </div>
      </div>
    </div>
  );
};

export default History;